/**
 * React hook for playground session SSE streaming
 *
 * Manages EventSource lifecycle for real-time playground execution updates:
 * - Connection establishment and cleanup
 * - Event parsing (action, action_success, action_failed, final_response)
 * - Auto-reconnection on errors
 * - Support for playground-specific event types
 *
 * Refactored to use existing session SSE endpoint (/api/sessions/{session_id}/stream)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { ParsedEvent } from '../types/session';

export type PlaygroundSSEStatus = 'connecting' | 'open' | 'closed' | 'error';

/**
 * Info about the tool call the agent is paused before in step mode.
 */
export interface StepModePendingInfo {
  tool_name: string;
  args: Record<string, any>;
  step_number: number;
}

/**
 * Result from usePlaygroundSSE hook
 */
export interface UsePlaygroundSSEResult {
  events: ParsedEvent[];
  status: PlaygroundSSEStatus;
  error: string | null;
  isRunning: boolean;
  streamingUrl: string | null;
  agentSpecId: string | null;
  reconnect: () => void;
  /** True when the agent is paused waiting for the user to click "Next". */
  waitingForConfirmation: boolean;
  /** Info about the pending tool call (only set when waitingForConfirmation). */
  pendingStepInfo: StepModePendingInfo | null;
  /** Whether this session was started in step mode. */
  isStepMode: boolean;
}

/**
 * Custom hook for managing SSE connection to playground session stream
 *
 * @param sessionId - The ID of the playground session to stream
 * @param enabled - Whether to establish the connection (default: true)
 * @returns Object containing events, connection status, error, running state, streaming URL, and reconnect function
 *
 * @example
 * const { events, status, error, isRunning, streamingUrl } = usePlaygroundSSE(sessionId);
 *
 * // Display streaming browser
 * {streamingUrl && <iframe src={streamingUrl} />}
 *
 * // Display events
 * {events.map((event, i) => <EventCard key={i} event={event} index={i} />)}
 */
export function usePlaygroundSSE(
  sessionId: string | undefined,
  enabled: boolean = true
): UsePlaygroundSSEResult {
  const [events, setEvents] = useState<ParsedEvent[]>([]);
  const [status, setStatus] = useState<PlaygroundSSEStatus>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [streamingUrl, setStreamingUrl] = useState<string | null>(null);
  const [agentSpecId, setAgentSpecId] = useState<string | null>(null);
  const [waitingForConfirmation, setWaitingForConfirmation] = useState<boolean>(false);
  const [pendingStepInfo, setPendingStepInfo] = useState<StepModePendingInfo | null>(null);
  const [isStepMode, setIsStepMode] = useState<boolean>(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Base API URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  /**
   * Close the EventSource connection and cleanup
   */
  const closeConnection = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  /**
   * Establish SSE connection to backend using existing session endpoint
   */
  const connect = useCallback(() => {
    if (!sessionId || !enabled) {
      return;
    }

    // Close existing connection if any
    closeConnection();

    // Clear old state when reconnecting
    setEvents([]);
    setStreamingUrl(null);
    setAgentSpecId(null);
    setIsRunning(false);
    setWaitingForConfirmation(false);
    setPendingStepInfo(null);
    setIsStepMode(false);

    try {
      setStatus('connecting');
      setError(null);

      // Get auth token from localStorage for SSE connection
      const token = localStorage.getItem('access_token');
      // Use existing session SSE endpoint instead of playground-specific endpoint
      const url = `${API_BASE_URL}/api/sessions/${sessionId}/stream${token ? `?token=${token}` : ''}`;
      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      // Connection opened
      eventSource.onopen = () => {
        setStatus('open');
      };

      // Listen for run_started event
      eventSource.addEventListener('run_started', (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data);
          setIsRunning(true);
          if (data.streaming_url) {
            setStreamingUrl(data.streaming_url);
          }
        } catch (err) {
          console.error('Failed to parse run_started event:', err);
        }
      });

      // Listen for initialize_event (contains streaming_url and agent_spec_id)
      eventSource.addEventListener('initialize_event', (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data);
          const initData = data.data || data;

          if (initData.streaming_url) {
            setStreamingUrl((prev) => prev || initData.streaming_url);
            setIsRunning(true);
          }
          if (initData.agent_spec_id) {
            setAgentSpecId((prev) => prev || initData.agent_spec_id);
          }
          // Detect step mode from initialize_event
          if (initData.step_mode) {
            setIsStepMode(true);
          }

          const event: ParsedEvent = {
            type: 'initialize_event',
            data: data,
            timestamp: Date.now() / 1000,
          };
          setEvents((prev) => [...prev, event]);
        } catch (err) {
          console.error('Failed to parse initialize_event:', err);
        }
      });

      // Listen for action events (processing state)
      eventSource.addEventListener('action', (e: MessageEvent) => {
        try {
          const event: ParsedEvent = JSON.parse(e.data);
          // When we receive an action event, the previous confirm was processed
          setWaitingForConfirmation(false);
          setPendingStepInfo(null);
          setEvents((prev) => [...prev, event]);
        } catch (err) {
          console.error('Failed to parse action event:', err);
        }
      });

      // Listen for action_success events
      eventSource.addEventListener('action_success', (e: MessageEvent) => {
        try {
          const event: ParsedEvent = JSON.parse(e.data);
          setEvents((prev) => [...prev, event]);

          // Check for streaming_url change in functionResponse.response.streaming_url
          const responseStreamingUrl = event.data?.response?.streaming_url;
          if (responseStreamingUrl) {
            setStreamingUrl(responseStreamingUrl);
          }
        } catch (err) {
          console.error('Failed to parse action_success event:', err);
        }
      });

      // Listen for action_failed events
      eventSource.addEventListener('action_failed', (e: MessageEvent) => {
        try {
          const event: ParsedEvent = JSON.parse(e.data);
          setEvents((prev) => [...prev, event]);
        } catch (err) {
          console.error('Failed to parse action_failed event:', err);
        }
      });

      // Listen for confirmation_required events (step mode)
      eventSource.addEventListener('confirmation_required', (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data);
          const info = data.data || data;
          setWaitingForConfirmation(true);
          setPendingStepInfo({
            tool_name: info.tool_name,
            args: info.args || {},
            step_number: info.step_number,
          });
        } catch (err) {
          console.error('Failed to parse confirmation_required event:', err);
        }
      });

      // Listen for step_replay_progress events (L2 step replay)
      eventSource.addEventListener('step_replay_progress', (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data);
          const event: ParsedEvent = {
            type: 'step_replay_progress',
            data: data.data || data,
            timestamp: Date.now() / 1000,
          };
          setEvents((prev) => [...prev, event]);
        } catch (err) {
          console.error('Failed to parse step_replay_progress event:', err);
        }
      });

      // Listen for final_response events
      eventSource.addEventListener('final_response', (e: MessageEvent) => {
        try {
          const event: ParsedEvent = JSON.parse(e.data);
          setEvents((prev) => [...prev, event]);
        } catch (err) {
          console.error('Failed to parse final_response event:', err);
        }
      });

      // Listen for complete event (sent by backend when session finishes)
      eventSource.addEventListener('complete', (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data);
          setIsRunning(false);
          setStatus('closed');
          closeConnection();

          // Trigger a refetch of the session to get final status and output
          // This ensures the UI updates from "running" to "completed/failed"
        } catch (err) {
          console.error('Failed to parse complete event:', err);
        }
      });

      // Listen for error events from backend
      eventSource.addEventListener('error', (e: MessageEvent) => {
        try {
          if (e.data) {
            const errorData = JSON.parse(e.data);
            setError(errorData.error || 'An error occurred during playground session');
          }
        } catch (err) {
          console.error('Failed to parse error event:', err);
        }
      });

      // Handle connection errors
      eventSource.onerror = (e) => {
        console.error('SSE connection error:', e);
        setStatus('error');
        setError('Connection lost to playground stream');
        closeConnection();
      };
    } catch (err) {
      console.error('Failed to create EventSource:', err);
      setStatus('error');
      setError('Failed to establish connection to playground stream');
      setIsRunning(false);
    }
  }, [sessionId, enabled, closeConnection, API_BASE_URL]);

  /**
   * Manual reconnection function
   */
  const reconnect = useCallback(() => {
    setEvents([]); // Clear existing events on manual reconnect
    setError(null);
    setStreamingUrl(null);
    setAgentSpecId(null);
    setWaitingForConfirmation(false);
    setPendingStepInfo(null);
    setIsStepMode(false);
    connect();
  }, [connect]);

  // Establish connection when sessionId changes or enabled becomes true
  useEffect(() => {
    if (sessionId && enabled) {
      connect();
    } else {
      // If disabled, close any existing connection
      closeConnection();
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      closeConnection();
    };
  }, [sessionId, enabled, connect, closeConnection]);

  return {
    events,
    status,
    error,
    isRunning,
    streamingUrl,
    agentSpecId,
    reconnect,
    waitingForConfirmation,
    pendingStepInfo,
    isStepMode,
  };
}
